<script setup>
import { ref, onMounted, computed, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import PipelinesAPI from 'dashboard/api/pipelines';
import MessageAPI from '../../../../api/inbox/message';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import { emitter } from 'shared/helpers/mitt';
import Button from '../../../../components-next/button/Button.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  currentStage: {
    type: String,
    required: true,
  },
  contact: {
    type: Object,
    default: () => ({}),
  },
  conversation: {
    type: Object,
    default: () => ({}),
  },
  // eslint-disable-next-line vue/no-unused-properties -- consumed by parent in conditional sends; keep for API stability
  item: {
    type: Object,
    required: true,
  },
  items: {
    type: Array,
    default: () => [],
  },
  isBulk: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'send']);
const { t } = useI18n();

const loading = ref(false);
const sendingMessage = ref(false);
const pipelines = ref([]);
const error = ref(null);
const messageError = ref(null);
const selectedTemplate = ref(null);
const showCustomMessage = ref(false);
const customMessage = ref('');

// Busca os funis e seus templates
const fetchPipelines = async () => {
  try {
    loading.value = true;
    const response = await PipelinesAPI.get();
    pipelines.value = response.data;
  } catch (err) {
    error.value = err.message;
  } finally {
    loading.value = false;
  }
};

// Retorna os templates da etapa atual
const currentStageTemplates = computed(() => {
  const templates = [];

  pipelines.value.forEach(pipeline => {
    if (pipeline.stages[props.currentStage]?.message_templates) {
      templates.push(...pipeline.stages[props.currentStage].message_templates);
    }
  });

  return templates;
});

// Retorna a cor da etapa atual
const currentStageColor = computed(() => {
  const pipeline = pipelines.value.find(f => f.stages[props.currentStage]);
  return pipeline?.stages[props.currentStage]?.color || '#E5E7EB';
});

const handleSendMessage = async () => {
  try {
    sendingMessage.value = true;
    const messageContent = showCustomMessage.value
      ? customMessage.value
      : selectedTemplate.value.content;

    if (props.isBulk) {
      // Enviar para múltiplos itens (use display_id for API calls)
      await Promise.all(
        props.items.map(item =>
          MessageAPI.create({
            conversationId: item.item_details.conversation.display_id,
            message: messageContent,
            private: false,
            message_type: 'outgoing',
            inbox_id: item.item_details.conversation?.inbox_id || 1,
          })
        )
      );
    } else {
      // Envio único
      await MessageAPI.create({
        conversationId: props.conversationId,
        message: messageContent,
        private: false,
        message_type: 'outgoing',
        inbox_id: props.conversation?.inbox_id || 1,
      });
    }

    emitter.emit('newToastMessage', {
      message: props.isBulk
        ? t('PIPELINE.MESSAGES.MESSAGES_SENT_SUCCESS')
        : t('PIPELINE.MESSAGES.MESSAGE_SENT_SUCCESS'),
      action: { type: 'success' },
    });

    emit('send');
  } catch (sendErr) {
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.SEND_MESSAGE.STATUS.SENDING'),
      action: { type: 'error' },
    });
    // Log detalhado do erro capturado
    error.value = sendErr.message;
  } finally {
    sendingMessage.value = false;
  }
};

const initialize = () => {
  fetchPipelines();
};

onMounted(() => {
  initialize();
});

onBeforeUnmount(() => {
  // Limpa os estados ao desmontar
  selectedTemplate.value = null;
  pipelines.value = [];
  error.value = null;
});

// Computed para formatar o nome do canal
const channelName = computed(() => {
  const channelType = props.conversation?.meta?.channel;
  const channelMap = {
    'Channel::WebWidget': {
      name: t('PIPELINE.CHANNELS.WEBSITE'),
    },
    'Channel::Api': {
      name: 'API',
    },
    'Channel::Email': {
      name: t('PIPELINE.CHANNELS.EMAIL'),
    },
    'Channel::TwitterProfile': {
      name: t('PIPELINE.CHANNELS.TWITTER'),
    },
    'Channel::FacebookPage': {
      name: t('PIPELINE.CHANNELS.FACEBOOK'),
    },
    'Channel::TelegramBot': {
      name: t('PIPELINE.CHANNELS.TELEGRAM'),
    },
    'Channel::WhatsApp': {
      name: t('PIPELINE.CHANNELS.WHATSAPP'),
    },
  };

  return channelMap[channelType] || { name: t('PIPELINE.CHANNELS.CHAT') };
});
</script>

<template>
  <div class="flex flex-col h-full">
    <div class="p-4 flex-1 overflow-y-auto max-h-[60vh]">
      <!-- Informações do Contato -->
      <div
        class="border border-dashed border-woot-500 dark:border-woot-400 rounded-lg p-4 mb-4 bg-woot-50/10 dark:bg-woot-900/10"
      >
        <!-- Cabeçalho com Informações Principais -->
        <div class="flex items-center gap-3 mb-3">
          <Avatar :src="contact?.thumbnail" :name="contact?.name" :size="40" />
          <div class="flex-1">
            <h4 class="text-sm font-medium text-woot-600 dark:text-woot-400">
              {{ contact?.name || t('PIPELINE.ITEM_CONVERSATION.NO_CONTACT') }}
            </h4>
            <div class="flex items-center gap-2 text-xs text-slate-500">
              <span v-if="contact?.email">
                <span
                  class="material-icons-outlined text-sm mr-1"
                  :aria-label="
                    t('PIPELINE.SEND_MESSAGE_TEMPLATE.ICON_MAIL_LABEL')
                  "
                >
                  {{ 'mail' }}
                </span>
                {{ t('PIPELINE.ITEM_CONVERSATION.CONTACT.EMAIL') }}:
                {{ contact.email }}
              </span>
              <span v-if="contact?.phone_number">
                <span
                  class="material-icons-outlined text-sm mr-1"
                  :aria-label="
                    t('PIPELINE.SEND_MESSAGE_TEMPLATE.ICON_PHONE_LABEL')
                  "
                >
                  {{ 'phone' }}
                </span>
                {{ t('PIPELINE.ITEM_CONVERSATION.CONTACT.PHONE') }}:
                {{ contact.phone_number }}
              </span>
            </div>
          </div>
          <div
            v-if="conversation?.unread_count"
            class="flex items-center justify-center h-6 min-w-[1.5rem] px-2 bg-red-500 text-white text-xs font-medium rounded-full"
          >
            {{
              conversation.unread_count > 9 ? '9+' : conversation.unread_count
            }}
          </div>
        </div>

        <!-- Detalhes da Conversa -->
        <div
          class="grid grid-cols-2 gap-4 mb-3 pt-3 border-t border-woot-200/50 dark:border-woot-700/50"
        >
          <div>
            <p
              class="text-[10px] uppercase tracking-wider text-woot-500 dark:text-woot-400 mb-1"
            >
              {{ t('PIPELINE.SEND_MESSAGE_TEMPLATE.CHANNEL_LABEL') }}
            </p>
            <div class="flex items-center gap-2">
              <span class="text-xs text-slate-700 dark:text-slate-300">
                {{ channelName.name }}
              </span>
            </div>
          </div>
          <div>
            <p
              class="text-[10px] uppercase tracking-wider text-woot-500 dark:text-woot-400 mb-1"
            >
              {{ t('PIPELINE.SEND_MESSAGE_TEMPLATE.STATUS_LABEL') }}
            </p>
            <div class="flex items-center gap-2">
              <span
                class="w-2 h-2 rounded-full"
                :class="{
                  'bg-slate-500': conversation?.status === 'open',
                  'bg-slate-500': conversation?.status === 'pending',
                  'bg-slate-500': conversation?.status === 'resolved',
                }"
              />
              <span
                class="text-xs capitalize text-slate-700 dark:text-slate-300"
              >
                {{
                  conversation?.status ||
                  t('PIPELINE.SEND_MESSAGE_TEMPLATE.STATUS_UNKNOWN')
                }}
              </span>
            </div>
          </div>
        </div>

        <!-- Rodapé com ID e Timestamps -->
        <div
          class="flex items-center justify-between text-[10px] pt-3 border-t border-woot-200/50 dark:border-woot-700/50"
        >
          <div class="text-woot-500 dark:text-woot-400">
            {{ t('PIPELINE.SEND_MESSAGE_TEMPLATE.CONVERSATION_PREFIX') }} #{{
              conversation?.id
            }}
          </div>
          <div class="flex items-center gap-3 text-slate-500">
            <span v-if="conversation?.created_at">
              {{ t('PIPELINE.SEND_MESSAGE_TEMPLATE.CREATED_AT_LABEL') }}
              {{ new Date(conversation.created_at).toLocaleDateString() }}
            </span>
            <span v-if="conversation?.last_activity_at">
              {{ t('PIPELINE.SEND_MESSAGE_TEMPLATE.LAST_ACTIVITY_LABEL') }}
              {{ new Date(conversation.last_activity_at).toLocaleDateString() }}
            </span>
          </div>
        </div>
      </div>

      <!-- Instruções -->
      <div v-if="!showCustomMessage" class="mb-4">
        <h4 class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          {{ t('PIPELINE.SEND_MESSAGE_TEMPLATE.SELECT_TEMPLATE') }}
        </h4>
        <p class="text-xs text-slate-500 dark:text-slate-400">
          {{ t('PIPELINE.SEND_MESSAGE_TEMPLATE.SELECT_TEMPLATE_HINT') }}
        </p>
      </div>

      <!-- Loading State -->
      <div
        v-if="loading && !showCustomMessage"
        class="flex items-center justify-center py-8"
      >
        <span
          class="w-8 h-8 border-2 border-slate-200 border-t-woot-500 rounded-full animate-spin"
        />
      </div>

      <!-- Error State -->
      <div
        v-else-if="error && !showCustomMessage"
        class="flex flex-col items-center justify-center py-8"
      >
        <fluent-icon icon="error" size="24" class="text-red-500 mb-2" />
        <p class="text-slate-600 dark:text-slate-400">
          {{ t('PIPELINE.SEND_MESSAGE_TEMPLATE.ERROR_LOADING') }}
        </p>
        <Button
          variant="ghost"
          color="slate"
          size="sm"
          class="mt-2"
          @click="fetchPipelines"
        >
          {{ t('PIPELINE.SEND_MESSAGE_TEMPLATE.RETRY') }}
        </Button>
      </div>

      <!-- Content -->
      <div v-else>
        <!-- Templates Grid -->
        <div
          v-if="currentStageTemplates.length && !showCustomMessage"
          class="space-y-4"
        >
          <div
            class="grid grid-cols-2 gap-4 p-2 rounded-lg overflow-y-auto max-h-[60vh]"
            :style="{
              backgroundColor: `${currentStageColor}15`,
              border: `1px solid ${currentStageColor}`,
            }"
          >
            <div
              v-for="template in currentStageTemplates"
              :key="template.id"
              class="p-4 rounded-lg border cursor-pointer bg-white dark:bg-slate-800"
              :class="
                selectedTemplate?.id === template.id
                  ? 'border-2 border-woot-500 dark:border-woot-400 bg-woot-50 dark:bg-woot-900/10'
                  : 'border-slate-200 dark:border-slate-700'
              "
              @click="selectedTemplate = template"
            >
              <div>
                <div class="flex items-center gap-2 mb-2">
                  <h5
                    class="flex-1 text-sm font-medium truncate"
                    :class="
                      selectedTemplate?.id === template.id
                        ? 'text-woot-600 dark:text-woot-400'
                        : 'text-slate-700 dark:text-slate-200'
                    "
                  >
                    {{ template.title }}
                  </h5>
                </div>
                <p
                  class="text-xs text-slate-500 dark:text-slate-400 line-clamp-2"
                  :title="template.content"
                >
                  {{ template.content }}
                </p>
                <div class="flex items-center gap-2 mt-2">
                  <span
                    v-if="template.webhook?.enabled"
                    class="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400"
                  >
                    {{ t('PIPELINE.SEND_MESSAGE_TEMPLATE.BADGE_WEBHOOK') }}
                  </span>
                  <span
                    v-if="template.conditions?.enabled"
                    class="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400"
                  >
                    {{ t('PIPELINE.SEND_MESSAGE_TEMPLATE.BADGE_CONDITIONS') }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          v-else-if="!showCustomMessage"
          class="text-sm text-center text-slate-500 dark:text-slate-400 py-4"
        >
          <div
            class="flex flex-col items-center justify-center py-8 text-center"
          >
            <fluent-icon
              icon="document"
              size="24"
              class="text-slate-400 dark:text-slate-600 mb-2"
            />
            <h4
              class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              {{ t('PIPELINE.MESSAGE_TEMPLATES.NO_TEMPLATES') }}
            </h4>
            <p class="text-xs text-slate-500 dark:text-slate-400">
              {{ t('PIPELINE.MESSAGE_TEMPLATES.NO_TEMPLATES_DESCRIPTION') }}
            </p>
          </div>
        </div>

        <!-- Mensagem Personalizada -->
        <div class="mt-4 flex justify-center">
          <button
            class="text-sm text-woot-500 hover:text-woot-600 dark:text-woot-400 dark:hover:text-woot-300 hover:underline"
            @click="showCustomMessage = !showCustomMessage"
          >
            {{
              showCustomMessage
                ? t('PIPELINE.SEND_MESSAGE.SELECT_TEMPLATE')
                : t('PIPELINE.SEND_MESSAGE.CUSTOM_MESSAGE')
            }}
          </button>
        </div>
        <div v-if="showCustomMessage" class="mt-2">
          <textarea
            v-model="customMessage"
            class="w-full p-3 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 placeholder-slate-400 dark:placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
            :placeholder="t('PIPELINE.SEND_MESSAGE.CUSTOM_MESSAGE_PLACEHOLDER')"
            rows="4"
          />
        </div>
      </div>
    </div>

    <footer class="p-4 border-t border-slate-200 dark:border-slate-700">
      <div v-if="messageError" class="mb-2 text-sm text-red-500 text-center">
        {{ messageError }}
      </div>
      <div class="flex justify-end gap-2">
        <Button variant="ghost" color="slate" size="sm" @click="emit('close')">
          {{ t('CANCEL') }}
        </Button>
        <Button
          variant="solid"
          color="blue"
          size="sm"
          :disabled="(!selectedTemplate && !customMessage) || sendingMessage"
          :is-loading="sendingMessage"
          @click="handleSendMessage"
        >
          {{
            sendingMessage
              ? t('PIPELINE.SEND_MESSAGE.STATUS.SENDING')
              : selectedTemplate
                ? t('PIPELINE.SEND_MESSAGE.ACTIONS.SEND_SELECTED', {
                    title: selectedTemplate.title,
                  })
                : customMessage
                  ? t('PIPELINE.SEND_MESSAGE.ACTIONS.SEND_CUSTOM')
                  : t('PIPELINE.SEND_MESSAGE.ACTIONS.SEND')
          }}
        </Button>
      </div>
    </footer>
  </div>
</template>
